import React from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEOHead from "../components/SEOHead";
import { Mail, Phone, MapPin } from "lucide-react";

const constructionWorker = "/images/Alkanes.png";

export default function Contact() {
  return (
    <div className="min-h-screen bg-gray-950">
      <SEOHead
        title="Contact Alkanes Engineering Limited - Lagos Nigeria | Engineering Services"
        description="Contact Alkanes Engineering Limited in Lagos, Nigeria. Professional engineering services for Oil & Gas, Power, Infrastructure. Phone: +234 (0) 802 556 8836. Email: info@alkanesengineering.com"
        keywords="Alkanes Engineering contact, engineering company Lagos, Alkanes Engineering Limited Nigeria, oil and gas engineering contact"
        canonical="https://alkanesengineering.com/contact"
      />
      <Header />

      {/* Hero Section */}
      <section
        className="relative overflow-hidden pt-16 pb-20 md:pt-24 md:pb-28"
        style={{
          backgroundImage: `url(${constructionWorker})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 bg-black/35"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-4 text-white">
            Contact Us
          </h1>
          <p className="text-lg text-gray-100 max-w-2xl">
            Get in touch with our team to discuss your project needs.
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 md:py-28 bg-gray-950">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Header */}
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Get in Touch with Us
            </h2>
            <p className="text-lg text-gray-300 max-w-2xl mx-auto">
              Have a project in mind? We'd love to hear from you. Reach out to
              our team to discuss your engineering and construction needs.
            </p>
          </div>

          {/* Contact Information Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-20">
            <div className="bg-gray-800 rounded-xl border border-gray-700 p-8 hover:border-primary transition-all hover:shadow-lg hover:shadow-primary/10">
              <div className="w-12 h-12 bg-primary rounded-lg flex items-center justify-center mb-4">
                <Phone className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-4">
                Phone Numbers
              </h3>
              <div className="space-y-3">
                <div>
                  <p className="text-gray-400 text-sm mb-1">Primary</p>
                  <a
                    href="tel:+234802556883"
                    className="text-white font-semibold hover:text-primary transition-colors"
                  >
                    +234 (0) 802 556 8836
                  </a>
                </div>
                <div>
                  <p className="text-gray-400 text-sm mb-1">Secondary</p>
                  <a
                    href="tel:+234802123456"
                    className="text-white font-semibold hover:text-primary transition-colors"
                  >
                    +234 (0) 802 123 4567
                  </a>
                </div>
              </div>
            </div>

            <div className="bg-gray-800 rounded-xl border border-gray-700 p-8 hover:border-primary transition-all hover:shadow-lg hover:shadow-primary/10">
              <div className="w-12 h-12 bg-primary rounded-lg flex items-center justify-center mb-4">
                <Mail className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-4">
                Email Address
              </h3>
              <div className="space-y-3">
                <div>
                  <p className="text-gray-400 text-sm mb-1">General Inquiries</p>
                  <a
                    href="mailto:info@alkaneseengineering.com"
                    className="text-white font-semibold hover:text-primary transition-colors block break-all"
                  >
                    info@alkaneseengineering.com
                  </a>
                </div>
                <div>
                  <p className="text-gray-400 text-sm mb-1">Enquiries</p>
                  <a
                    href="mailto:enquiries@alkanesengineering.com"
                    className="text-white font-semibold hover:text-primary transition-colors block break-all"
                  >
                    enquiries@alkanesengineering.com
                  </a>
                </div>
              </div>
              <p className="text-gray-400 text-sm mt-4">
                We'll respond to your inquiry within 24 hours.
              </p>
            </div>

            <div className="bg-gray-800 rounded-xl border border-gray-700 p-8 hover:border-primary transition-all hover:shadow-lg hover:shadow-primary/10">
              <div className="w-12 h-12 bg-primary rounded-lg flex items-center justify-center mb-4">
                <MapPin className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-4">
                Office Location
              </h3>
              <p className="text-white font-semibold mb-2">
                Alkane Engineering Limited
              </p>
              <p className="text-gray-400">Lagos, Nigeria</p>
              <p className="text-gray-400 text-sm mt-4">
                Available for project consultations and site visits.
              </p>
            </div>
          </div>

          {/* Quick Contact Form */}
          <div className="bg-gray-800 rounded-xl border border-gray-700 p-8 md:p-12">
            <h3 className="text-2xl font-bold text-white mb-8">
              Send us a Message
            </h3>
            <form className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-white mb-2">
                    Full Name
                  </label>
                  <input
                    type="text"
                    placeholder="Your full name"
                    className="w-full px-4 py-3 bg-gray-950 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-white mb-2">
                    Email Address
                  </label>
                  <input
                    type="email"
                    placeholder="your@email.com"
                    className="w-full px-4 py-3 bg-gray-950 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-white mb-2">
                  Phone Number
                </label>
                <input
                  type="tel"
                  placeholder="+234 (0) XXX XXX XXXX"
                  className="w-full px-4 py-3 bg-gray-950 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-white mb-2">
                  Project Type
                </label>
                <select className="w-full px-4 py-3 bg-gray-950 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary">
                  <option value="">Select a service</option>
                  <option value="engineering">Engineering Services</option>
                  <option value="procurement">Procurement</option>
                  <option value="construction">Construction</option>
                  <option value="installation">Installation</option>
                  <option value="leasing">Equipment Leasing</option>
                  <option value="safety">Safety Equipment</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-white mb-2">
                  Message
                </label>
                <textarea
                  placeholder="Tell us about your project..."
                  rows={5}
                  className="w-full px-4 py-3 bg-gray-950 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                ></textarea>
              </div>

              <button
                type="submit"
                className="w-full bg-primary text-white py-3 rounded-lg font-semibold transition-all hover:shadow-lg hover:shadow-primary/40 active:scale-95"
              >
                Send Message
              </button>
            </form>
          </div>

          {/* Additional Info */}
          <div className="mt-16 bg-primary/10 border border-primary/30 rounded-xl p-8">
            <h3 className="text-xl font-bold text-white mb-4">
              Business Hours
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <p className="text-gray-300">
                  <span className="font-semibold">Monday - Friday:</span> 8:00
                  AM - 5:00 PM (WAT)
                </p>
              </div>
              <div>
                <p className="text-gray-300">
                  <span className="font-semibold">Weekend:</span> By appointment
                  only
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
